import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, auth, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Activity, ShieldCheck, Users, X } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const [tab, setTab] = useState("client");
  const [busy, setBusy] = useState(false);
  // Disclaimer must appear before EVERY login attempt — no localStorage memoization.
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  const ackDisclaimer = () => {
    setShowDisclaimer(false);
  };

  // client form
  const [clientId, setClientId] = useState("");
  const [clientPw, setClientPw] = useState("");

  // admin form
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [otp, setOtp] = useState("");
  const [otpRequired, setOtpRequired] = useState(false);

  // Show one-shot "logged in from another device" banner if we were force-logged-out
  const [forcedLogoutReason, setForcedLogoutReason] = useState(() => {
    try {
      const r = sessionStorage.getItem("aditi_logout_reason");
      if (r) sessionStorage.removeItem("aditi_logout_reason");
      return r || "";
    } catch (_) { return ""; }
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let r;
      if (tab === "admin") {
        const payload = { email, password: pw };
        if (otpRequired || otp) payload.otp = otp;
        r = await api.post("/auth/admin/login", payload);
      } else {
        // Best-effort geolocation capture (silent if user denies)
        const geo = await new Promise((resolve) => {
          if (!("geolocation" in navigator)) return resolve({});
          let done = false;
          const timer = setTimeout(() => { if (!done) { done = true; resolve({}); } }, 4000);
          navigator.geolocation.getCurrentPosition(
            (p) => { if (done) return; done = true; clearTimeout(timer);
              resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }); },
            () => { if (done) return; done = true; clearTimeout(timer); resolve({}); },
            { enableHighAccuracy: false, timeout: 3500, maximumAge: 60000 }
          );
        });
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        r = await api.post("/auth/client/login", {
          client_id: clientId, password: clientPw,
          ...geo, timezone: tz, user_agent: navigator.userAgent,
        });
      }
      auth.setToken(r.data.token);
      auth.setUser(r.data.user);
      toast.success(`Welcome ${r.data.user.name}`);
      nav(r.data.user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      const msg = errMsg(err);
      if (msg === "OTP_REQUIRED") {
        setOtpRequired(true);
        toast.info("Enter your 6-digit authenticator code", { duration: 4000 });
      } else {
        toast.error("Login failed", { description: msg });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-secondary/30">
      {/* Disclaimer modal — smartgain24-style: orange header bar + white body + X close */}
      {showDisclaimer && (
        <div className="fixed inset-0 z-[100] bg-slate-900/50 grid place-items-center p-4"
             data-testid="disclaimer-overlay">
          <div className="bg-white rounded-md shadow-2xl max-w-md w-full overflow-hidden border border-slate-200"
               data-testid="disclaimer-modal">
            {/* Orange header bar */}
            <div className="flex items-center justify-between bg-[#f58a22] px-4 py-2.5">
              <h2 className="text-base font-semibold text-white tracking-wide">Disclaimer !</h2>
              <button onClick={ackDisclaimer}
                      data-testid="disclaimer-close-x"
                      className="text-white hover:text-white/80 focus:outline-none"
                      aria-label="Close disclaimer">
                <X className="h-4 w-4" strokeWidth={3} />
              </button>
            </div>
            {/* White body */}
            <div className="bg-white px-5 py-5">
              <p className="text-[13px] text-slate-700 leading-[1.55]">
                The information and data provided on this application are without warranty of any kind.
                This application is meant to be used for educational and training purposes only.{" "}
                <span className="font-bold">No Real Money Involved.</span>
              </p>
              <div className="mt-5 flex justify-end">
                <button onClick={ackDisclaimer}
                        data-testid="disclaimer-proceed"
                        className="px-5 py-1.5 bg-[#f58a22] hover:bg-[#e07a14] text-white text-[13px] font-semibold rounded-sm transition-colors">
                  I Agree
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-amber-50">
        <div className="absolute -top-20 -right-20 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-amber-200/30 blur-3xl" />

        <div className="relative">
          <div className="flex items-center gap-2">
            <img src="/logo192.png" alt="ADITI5" className="brand-logo h-11 w-11 rounded-xl" />
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight">ADITI5</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Trading Platform</div>
            </div>
          </div>
        </div>

        <div className="relative max-w-md">
          <h1 className="text-4xl font-extrabold tracking-tight leading-tight">
            Real-time market.<br />
            <span className="text-primary">Zero-risk practice.</span>
          </h1>
          <p className="mt-4 text-muted-foreground text-base">
            Trade NSEFO &amp; MCX futures on simulated live ticks — circuit-validated targets, brokerage rules, and admin-supervised paper accounts.
          </p>

          <div className="grid grid-cols-3 gap-3 mt-8">
            <Feat icon={Activity} title="Live ticks" desc="Bid / Ask / LTP / Range" />
            <Feat icon={ShieldCheck} title="Circuit-safe" desc="Target ≤ UC, SL ≥ LC" />
            <Feat icon={Users} title="50 students" desc="Per-client lot &amp; balance" />
          </div>
        </div>

        <div className="relative text-xs text-muted-foreground">
          © {new Date().getFullYear()} ADITI5. For educational use only.
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-md bg-white border border-border rounded-2xl p-8 shadow-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <img src="/logo192.png" alt="ADITI5" className="brand-logo h-10 w-10 rounded-lg" />
            <div>
              <div className="text-sm font-bold">ADITI5</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Trading Terminal</div>
            </div>
          </div>

          <h2 className="text-2xl font-bold tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Choose your access type to continue.</p>

          {forcedLogoutReason && (
            <div data-testid="forced-logout-banner"
                 className="mb-4 rounded-lg border border-loss/40 bg-loss/10 px-3 py-2 text-xs text-loss">
              <div className="font-bold uppercase tracking-wider text-[10px] mb-0.5">Signed out</div>
              <div>{forcedLogoutReason}</div>
            </div>
          )}

          <div className="grid grid-cols-2 mb-5 bg-secondary rounded-lg p-1">
            <button type="button" onClick={() => setTab("client")}
                    data-testid="tab-client"
                    className={`h-9 rounded-md text-sm font-semibold transition-colors ${tab === "client" ? "bg-white shadow-sm" : "text-muted-foreground"}`}>
              Client
            </button>
            <button type="button" onClick={() => setTab("admin")}
                    data-testid="tab-admin"
                    className={`h-9 rounded-md text-sm font-semibold transition-colors ${tab === "admin" ? "bg-white shadow-sm" : "text-muted-foreground"}`}>
              Admin
            </button>
          </div>

          {tab === "client" ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="cid" className="text-xs uppercase tracking-wider">Client ID</Label>
                <Input id="cid" value={clientId} onChange={(e) => setClientId(e.target.value)}
                       placeholder="ADITI501" required data-testid="login-client-id"
                       className="font-mono uppercase" />
              </div>
              <div>
                <Label htmlFor="cpw" className="text-xs uppercase tracking-wider">Password</Label>
                <Input id="cpw" type="password" value={clientPw} onChange={(e) => setClientPw(e.target.value)}
                       required data-testid="login-client-password" />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="em" className="text-xs uppercase tracking-wider">Email</Label>
                <Input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="admin@aditi.com" required data-testid="login-admin-email" />
              </div>
              <div>
                <Label htmlFor="pw" className="text-xs uppercase tracking-wider">Password</Label>
                <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                       required data-testid="login-admin-password" />
              </div>
              {otpRequired && (
                <div data-testid="login-otp-row">
                  <Label htmlFor="otp" className="text-xs uppercase tracking-wider">Authenticator Code</Label>
                  <Input id="otp" inputMode="numeric" maxLength={6} value={otp}
                         onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                         placeholder="6-digit code" required autoFocus data-testid="login-admin-otp"
                         className="font-mono tracking-[0.4em] text-center" />
                  <p className="text-[10px] text-muted-foreground mt-1">Open your authenticator app (Google Authenticator / Authy) and enter the current 6-digit code.</p>
                </div>
              )}
            </div>
          )}

          <Button type="submit" disabled={busy}
                  data-testid="login-submit"
                  className="w-full mt-6 h-11 font-semibold">
            {busy ? "Signing in…" : `Sign in as ${tab === "admin" ? "Admin" : "Client"}`}
          </Button>

          <p className="mt-4 text-xs text-muted-foreground text-center">
            {tab === "client"
              ? "Client credentials are issued by your administrator."
              : "Admin access only. Contact ops if you need a reset."}
          </p>
        </form>
      </div>
    </div>
  );
}

function Feat({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-xl bg-white border border-border p-3">
      <Icon className="h-4 w-4 text-primary" />
      <div className="text-xs font-semibold mt-2">{title}</div>
      <div className="text-[10px] text-muted-foreground">{desc}</div>
    </div>
  );
}
